import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import {
  benefitsFromTags,
  type LevelBenefitId,
  type LevelBenefitTag,
} from '@/utils/level-benefits';

/**
 * Benefícios ativos do jogador para a barra de nível do header (2X COINS,
 * 2X XP, CASHBACK).
 *
 * A fonte é o `core_public_tags` do perfil da Smartico, que a store
 * `smarticoUserInfos` já carrega no `GetUserProfile` e re-mergeia nos eventos
 * `identify` e `props_change`. Isso é de graça aqui: quando o core liga ou
 * desliga uma tag, os chips acendem/apagam sem reload e sem ninguém pedir.
 *
 * O que NÃO tratar como "não tem benefício": lista vazia. Enquanto o SDK não
 * carregou e o `identify` não concluiu, todo jogador parece sem benefício —
 * a mesma janela cega documentada em `useVipStatus`. Para ENFEITAR (chip,
 * selo) isso basta, porque aparece quando o dado chega; para NEGAR algo, não
 * use este composable — pergunte a quem responde por direito de acesso.
 *
 * Nível e elo não vêm daqui: nível é a régua da Smartico
 * (`useLevelsProgress`) e o tier VIP é o `vip` de `GET /players/me`
 * (`useVipStatus`).
 */
export function useLevelBenefits() {
  const { userInfosProfile } = storeToRefs(useSmarticoUserInfos());

  /** Na ordem que o core mandou, e sem vão entre elas. */
  const activeBenefits = computed<LevelBenefitTag[]>(() =>
    benefitsFromTags(userInfosProfile.value?.core_public_tags),
  );

  const activeBenefitIds = computed(
    () => new Set(activeBenefits.value.map((benefit) => benefit.id)),
  );

  const hasAnyBenefit = computed(() => activeBenefits.value.length > 0);

  function hasBenefit(id: LevelBenefitId) {
    return activeBenefitIds.value.has(id);
  }

  return { activeBenefits, hasAnyBenefit, hasBenefit };
}
